import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  fetchOrganizationDetails,
  updateOrganization,
  fetchAllOrganizationsList,
} from '../../../actioncreators/actioncreators';
import { setSuperAdminOrgFilter } from '../../../actions/loanAction';
import CountryDropdown from '../../../fields/CountryDropdown';
import StateDropdown from '../../../fields/StateDropdown';
import CityDropdown from '../../../fields/CityDropdown';
import PincodeDropdown from '../../../fields/PincodeDropdown';

const organizationSizes = ["1-10", "11-50", "51-200", "201-1000", "1000+"];
const organizationTypes = ["Govt. Office", "IT", "Finance", "Healthcare", "Education", "Manufacturing", "Retail", "Other"];

/* ── Inline styles (mirrors CreateOrganization.jsx for visual consistency) ── */
const styles = {
  page: { minHeight: '100vh', backgroundColor: '#f1f5f9', padding: '24px' },
  backBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '6px', border: 'none',
    background: 'none', color: '#64748b', fontSize: '13px', cursor: 'pointer',
    padding: '4px 0', marginBottom: '16px', fontWeight: 500,
  },
  pageTitle: { fontSize: '22px', fontWeight: 700, color: '#0f172a', margin: '0 0 2px 0' },
  pageSubtitle: { fontSize: '13px', color: '#64748b', margin: '0 0 24px 0' },
  card: { backgroundColor: '#ffffff', borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' },
  sectionHeader: { display: 'flex', alignItems: 'center', gap: '10px', padding: '18px 24px 14px', borderBottom: '1px solid #f1f5f9' },
  sectionIcon: { width: '32px', height: '32px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', flexShrink: 0 },
  sectionLabel: { fontSize: '13px', fontWeight: 700, color: '#0f172a', margin: 0, letterSpacing: '0.1px' },
  sectionSubLabel: { fontSize: '11px', color: '#94a3b8', margin: 0 },
  sectionBody: { padding: '20px 24px' },
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '18px' },
  fieldLabel: { display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '5px', letterSpacing: '0.1px' },
  input: {
    width: '100%', height: '38px', padding: '0 12px', border: '1px solid #e2e8f0',
    borderRadius: '8px', fontSize: '13px', color: '#0f172a', backgroundColor: '#f8fafc',
    outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s', boxSizing: 'border-box',
  },
  inputError: { borderColor: '#ef4444' },
  select: {
    width: '100%', height: '38px', padding: '0 12px', border: '1px solid #e2e8f0',
    borderRadius: '8px', fontSize: '13px', color: '#0f172a', backgroundColor: '#f8fafc',
    outline: 'none', cursor: 'pointer', boxSizing: 'border-box',
  },
  colorInput: {
    width: '100%', height: '38px', padding: '2px', border: '1px solid #e2e8f0',
    borderRadius: '8px', backgroundColor: '#f8fafc', outline: 'none', cursor: 'pointer',
    boxSizing: 'border-box',
  },
  divider: { height: '1px', backgroundColor: '#f1f5f9', border: 'none', margin: 0 },
  footer: { display: 'flex', alignItems: 'center', gap: '12px', padding: '20px 24px', borderTop: '1px solid #f1f5f9', backgroundColor: '#fafbfc' },
  submitBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 28px',
    backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: '8px',
    fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s',
  },
  cancelBtn: {
    display: 'inline-flex', alignItems: 'center', padding: '10px 20px', backgroundColor: '#fff',
    color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px',
    fontWeight: 500, cursor: 'pointer',
  },
  errorText: { fontSize: '11px', color: '#ef4444', marginTop: '4px' },
};

const Field = ({ label, error, children }) => (
  <div>
    <label style={styles.fieldLabel}>{label}</label>
    {children}
    {error && <p style={styles.errorText}>{error}</p>}
  </div>
);

const SectionHeader = ({ icon, bg, title, subtitle }) => (
  <div style={styles.sectionHeader}>
    <div style={{ ...styles.sectionIcon, backgroundColor: bg }}>{icon}</div>
    <div>
      <p style={styles.sectionLabel}>{title}</p>
      {subtitle && <p style={styles.sectionSubLabel}>{subtitle}</p>}
    </div>
  </div>
);

const EMAIL_RE = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

const validateEmail = (raw) => {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (!EMAIL_RE.test(trimmed)) return 'Invalid email format (e.g. user@example.com)';
  return '';
};

const validateGSTNumber = (gst) => {
  const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
  if (!gst) return { isValid: true, message: '' };
  if (!gstRegex.test(gst)) return { isValid: false, message: 'Invalid GST Number format' };
  return { isValid: true, message: '' };
};

const EditOrganization = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const superAdminSelectedOrgId = useSelector((state) => state.superAdminSelectedOrgId);
  const superAdminSelectedOrgData = useSelector((state) => state.superAdminSelectedOrgData);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [allOrgs, setAllOrgs] = useState([]);
  const [emailError, setEmailError] = useState('');
  const [gstError, setGstError] = useState('');

  const [formData, setFormData] = useState({
    org_name: '',
    email: '',
    mobile: '',
    org_type: '',
    org_size: '',
    gst_number: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    district: '',
    state: '',
    country: 'India',
    countryCode: 'IN',
    pincode: '',
    branding_app_name: '',
    branding_primary_color: '#6366f1',
    branding_accent_color: '#f59e0b',
    branding_footer_text: '',
    parent_org_id: '',
    beacon_tracking_enabled: true,
  });

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      setLoading(true);
      try {
        const [detailsRes, orgsRes] = await Promise.all([
          fetchOrganizationDetails(orgId),
          fetchAllOrganizationsList().catch(() => null),
        ]);
        const org = detailsRes.data?.data || detailsRes?.data || null;
        if (!org) {
          setNotFound(true);
          return;
        }
        setFormData((prev) => ({
          ...prev,
          org_name: org.org_name || '',
          email: org.email || '',
          mobile: org.mobile || '',
          org_type: org.org_type || '',
          org_size: org.org_size || '',
          gst_number: org.gst_number || '',
          address_line_1: org.address_line_1 || '',
          address_line_2: org.address_line_2 || '',
          city: org.city || '',
          district: org.district || '',
          state: org.state || '',
          country: org.country || 'India',
          pincode: org.pincode || '',
          branding_app_name: org.branding_app_name || '',
          branding_primary_color: org.branding_primary_color || '#6366f1',
          branding_accent_color: org.branding_accent_color || '#f59e0b',
          branding_footer_text: org.branding_footer_text || '',
          parent_org_id: org.parent_org_id || '',
          // Organizations created before the toggle existed have no value; they keep BLE.
          beacon_tracking_enabled: org.beacon_tracking_enabled !== false,
        }));
        if (orgsRes) {
          const list = orgsRes.data?.data || [];
          setAllOrgs(list.filter((o) => o.org_id !== orgId));
        }
      } catch (err) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'email') setEmailError(validateEmail(value));
    if (name === 'gst_number') {
      const v = validateGSTNumber(value);
      setGstError(v.isValid ? '' : v.message);
    }
  };

  const handleEmailBlur = (e) => {
    const trimmed = e.target.value.trim();
    setFormData((prev) => ({ ...prev, email: trimmed }));
    setEmailError(validateEmail(trimmed));
  };

  const handleCountryChange = (selectedCountry) => {
    setFormData((prev) => ({
      ...prev,
      country: selectedCountry.name,
      countryCode: selectedCountry.code,
      state: '',
      city: '',
    }));
  };

  const handleStateChange = (e) => {
    setFormData((prev) => ({ ...prev, state: e.target.value, city: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedEmail = formData.email.trim();
    const emailErr = validateEmail(trimmedEmail);
    if (emailErr) {
      setEmailError(emailErr);
      return;
    }
    const gstValidation = validateGSTNumber(formData.gst_number);
    if (!gstValidation.isValid) {
      setGstError(gstValidation.message);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        org_name: formData.org_name,
        email: trimmedEmail,
        mobile: formData.mobile,
        org_type: formData.org_type,
        org_size: formData.org_size,
        gst_number: formData.gst_number,
        address_line_1: formData.address_line_1,
        address_line_2: formData.address_line_2,
        city: formData.city,
        district: formData.district,
        state: formData.state,
        country: formData.country,
        pincode: formData.pincode,
        branding_app_name: formData.branding_app_name,
        branding_primary_color: formData.branding_primary_color,
        branding_accent_color: formData.branding_accent_color,
        branding_footer_text: formData.branding_footer_text,
        parent_org_id: formData.parent_org_id || null,
        beacon_tracking_enabled: Boolean(formData.beacon_tracking_enabled),
      };
      await updateOrganization(orgId, payload);

      // Keep the header's org selector in sync. superAdminSelectedOrgData is
      // set once (on selection, or on creation) and never refreshed - so
      // without this, renaming the org that's currently selected in the
      // header left the header pill showing the OLD name even though the
      // save succeeded and the details/list pages (which re-fetch) showed
      // the new one. This looked exactly like the edit silently failing.
      if (superAdminSelectedOrgId === orgId) {
        dispatch(setSuperAdminOrgFilter(orgId, {
          ...superAdminSelectedOrgData,
          org_id: orgId,
          org_name: formData.org_name,
        }));
      }

      toast.success('Organization updated successfully');
      navigate(`/home/super-admin/organizations/${orgId}`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update organization');
    } finally {
      setSaving(false);
    }
  };

  const dropdownClass = "mt-0 block w-full rounded-md border border-gray-300 shadow-sm text-xs";

  if (loading) return (
    <div style={{ ...styles.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spinner animation="border" /> <span style={{ marginLeft: '12px', color: '#64748b' }}>Loading...</span>
    </div>
  );

  if (notFound) return (
    <div style={{ ...styles.page, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: '#64748b', marginBottom: '16px' }}>Organization not found</p>
      <button style={styles.submitBtn} onClick={() => navigate('/home/super-admin/organizations')}>Back to Organizations</button>
    </div>
  );

  return (
    <div style={styles.page}>
      <button type="button" style={styles.backBtn} onClick={() => navigate(`/home/super-admin/organizations/${orgId}`)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M19 12H5M12 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        Back to Organization Details
      </button>

      <h1 style={styles.pageTitle}>Edit Organization</h1>
      <p style={styles.pageSubtitle}>{formData.org_name}</p>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* ── Organization Details ── */}
          <div style={styles.card}>
            <SectionHeader icon="🏢" bg="#dbeafe" title="Organization Details" subtitle="Basic information about the organization" />
            <div style={styles.sectionBody}>
              <div style={styles.grid3}>
                <Field label="Organization Name *">
                  <input style={styles.input} type="text" name="org_name" value={formData.org_name} onChange={handleInputChange} required />
                </Field>
                <Field label="Email" error={emailError}>
                  <input
                    style={{ ...styles.input, ...(emailError ? styles.inputError : {}) }}
                    type="text" name="email" value={formData.email}
                    onChange={handleInputChange} onBlur={handleEmailBlur}
                  />
                </Field>
                <Field label="Mobile">
                  <input style={styles.input} type="text" name="mobile" value={formData.mobile} onChange={handleInputChange} />
                </Field>
                <Field label="Organization Type">
                  <select style={styles.select} name="org_type" value={formData.org_type} onChange={handleInputChange}>
                    <option value="">Select type</option>
                    {organizationTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Organization Size">
                  <select style={styles.select} name="org_size" value={formData.org_size} onChange={handleInputChange}>
                    <option value="">Select size</option>
                    {organizationSizes.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="GST Number (optional)" error={gstError}>
                  <input
                    style={{ ...styles.input, ...(gstError ? styles.inputError : {}) }}
                    type="text" name="gst_number" value={formData.gst_number}
                    onChange={handleInputChange} placeholder="22AAAAA0000A1Z5"
                  />
                </Field>
                <Field label="Parent Organization">
                  <select style={styles.select} name="parent_org_id" value={formData.parent_org_id} onChange={handleInputChange}>
                    <option value="">No parent (top-level)</option>
                    {allOrgs.map(o => <option key={o.org_id} value={o.org_id}>{o.org_name}</option>)}
                  </select>
                </Field>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '16px' }}>
                <input
                  type="checkbox"
                  id="beacon_tracking_enabled"
                  checked={formData.beacon_tracking_enabled}
                  onChange={(e) => setFormData(prev => ({ ...prev, beacon_tracking_enabled: e.target.checked }))}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <label htmlFor="beacon_tracking_enabled" style={{ fontSize: '14px', color: '#374151', cursor: 'pointer' }}>
                  Enable BLE (Beacon) Attendance
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                    When on, this organization gets the Beacons menu, the User Presence tab, student alerts and all BLE permissions. When off, all of these are hidden and BLE access is blocked. Existing data is kept.
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* ── Address ── */}
          <div style={styles.card}>
            <SectionHeader icon="📍" bg="#dcfce7" title="Address" subtitle="Registered office location" />
            <div style={styles.sectionBody}>
              <div style={styles.grid3}>
                <Field label="Address Line 1">
                  <input style={styles.input} type="text" name="address_line_1" value={formData.address_line_1} onChange={handleInputChange} />
                </Field>
                <Field label="Address Line 2">
                  <input style={styles.input} type="text" name="address_line_2" value={formData.address_line_2} onChange={handleInputChange} />
                </Field>
                <Field label="Country">
                  <CountryDropdown name="country" value={formData.country} onChange={handleCountryChange} className={dropdownClass} />
                </Field>
                <Field label="State / Province">
                  <StateDropdown name="state" countryCode={formData.countryCode} value={formData.state} onChange={handleStateChange} className={dropdownClass} />
                </Field>
                <Field label="City">
                  <CityDropdown name="city" countryName={formData.country} stateName={formData.state} value={formData.city} onChange={handleInputChange} className={dropdownClass} />
                </Field>
                <Field label="District">
                  <input style={styles.input} type="text" name="district" value={formData.district} onChange={handleInputChange} />
                </Field>
                <Field label="Pincode / Zipcode">
                  <PincodeDropdown
                    name="pincode" countryName={formData.country} stateName={formData.state} cityName={formData.city}
                    value={formData.pincode} onChange={handleInputChange}
                    className="mt-0 block w-full rounded-md border border-gray-300 h-9 text-xs px-3"
                  />
                </Field>
              </div>
            </div>
          </div>

          {/* ── Branding ── */}
          <div style={styles.card}>
            <SectionHeader icon="🎨" bg="#fce7f3" title="Branding" subtitle="Custom app name and colors shown to this organization's users" />
            <div style={styles.sectionBody}>
              <div style={styles.grid3}>
                <Field label="App Display Name">
                  <input style={styles.input} type="text" name="branding_app_name" value={formData.branding_app_name} onChange={handleInputChange} placeholder="arthaTRACK" />
                </Field>
                <Field label="Primary Color">
                  <input style={styles.colorInput} type="color" name="branding_primary_color" value={formData.branding_primary_color} onChange={handleInputChange} />
                </Field>
                <Field label="Accent Color">
                  <input style={styles.colorInput} type="color" name="branding_accent_color" value={formData.branding_accent_color} onChange={handleInputChange} />
                </Field>
                <Field label="Footer Text">
                  <input style={styles.input} type="text" name="branding_footer_text" value={formData.branding_footer_text} onChange={handleInputChange} placeholder="© 2026 Organization Name" />
                </Field>
              </div>
            </div>
          </div>

        </div>

        <div style={styles.footer}>
          <button type="submit" disabled={saving} style={{ ...styles.submitBtn, opacity: saving ? 0.7 : 1 }}>
            {saving
              ? <><Spinner size="sm" />Saving...</>
              : <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  Save Changes
                </>
            }
          </button>
          <button type="button" disabled={saving} style={styles.cancelBtn} onClick={() => navigate(`/home/super-admin/organizations/${orgId}`)}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditOrganization;
